import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { ArrowLeft, Plus, List, Columns3, Trash2, MessageSquare, Calendar, User, Sparkles, Paperclip, Download, X, Bell, BellOff, Filter, CheckSquare, Square } from 'lucide-react';
import AITaskCreator from '@/components/AITaskCreator';
import ImageWithAuth from '@/components/ImageWithAuth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusLabels = {
  todo: 'Zu erledigen',
  in_progress: 'In Bearbeitung',
  review: 'Review',
  done: 'Erledigt'
};

const priorityLabels = {
  low: 'Niedrig',
  medium: 'Mittel',
  high: 'Hoch',
  urgent: 'Dringend'
};

// Inline-editable Task Row Component
const TaskRow = ({ 
  task, 
  selectedTaskIds, 
  toggleTaskSelection, 
  handleUpdateTask, 
  openTaskDetail,
  statusLabels,
  priorityLabels,
  getProjectUsers
}) => {
  const [editingField, setEditingField] = useState(null);
  const [editTitle, setEditTitle] = useState(task.title);

  const handleTitleSave = () => {
    if (editTitle.trim() && editTitle !== task.title) {
      handleUpdateTask(task.id, { title: editTitle.trim() });
    }
    setEditingField(null);
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleTitleSave();
    } else if (e.key === 'Escape') {
      setEditTitle(task.title);
      setEditingField(null);
    }
  };

  return (
    <tr 
      data-testid={`task-row-${task.id}`}
      onClick={() => openTaskDetail(task.id)}
      className="cursor-pointer hover:bg-gray-50"
    >
      <td onClick={(e) => e.stopPropagation()} className="w-12">
        <input
          type="checkbox"
          checked={selectedTaskIds.includes(task.id)}
          onChange={() => toggleTaskSelection(task.id)}
          className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
        />
      </td>
      
      {/* Title - Click to edit */}
      <td>
        {editingField === 'title' ? (
          <div onClick={(e) => e.stopPropagation()} className="inline-block">
            <Input
              autoFocus
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              onBlur={handleTitleSave}
              onKeyDown={handleTitleKeyDown}
              className="h-8 font-semibold w-auto min-w-[150px]"
            />
          </div>
        ) : (
          <span 
            className="font-semibold text-gray-800 hover:text-blue-600 inline-block px-1 py-0.5 rounded hover:bg-blue-50"
            onClick={(e) => {
              e.stopPropagation();
              setEditTitle(task.title);
              setEditingField('title');
            }}
            title="Klicken zum Bearbeiten"
          >
            {task.title}
          </span>
        )}
      </td>
      
      {/* Status - Click to edit */}
      <td>
        {editingField === 'status' ? (
          <div onClick={(e) => e.stopPropagation()} className="inline-block">
            <Select
              value={task.status}
              onValueChange={(value) => {
                handleUpdateTask(task.id, { status: value });
                setEditingField(null);
              }}
              open={true}
              onOpenChange={(open) => !open && setEditingField(null)}
            >
              <SelectTrigger className="h-8 text-xs w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(statusLabels).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <span 
            className={`status-badge status-${task.status} cursor-pointer hover:ring-2 hover:ring-blue-300 hover:ring-offset-1 inline-block`}
            onClick={(e) => {
              e.stopPropagation();
              setEditingField('status');
            }}
            title="Klicken zum Ändern"
          >
            {statusLabels[task.status]}
          </span>
        )}
      </td>
      
      {/* Priority - Click to edit */}
      <td>
        {editingField === 'priority' ? (
          <div onClick={(e) => e.stopPropagation()} className="inline-block">
            <Select
              value={task.priority}
              onValueChange={(value) => {
                handleUpdateTask(task.id, { priority: value });
                setEditingField(null);
              }}
              open={true}
              onOpenChange={(open) => !open && setEditingField(null)}
            >
              <SelectTrigger className="h-8 text-xs w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(priorityLabels).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <span 
            className={`priority-badge priority-${task.priority} cursor-pointer hover:ring-2 hover:ring-blue-300 hover:ring-offset-1 inline-block`}
            onClick={(e) => {
              e.stopPropagation();
              setEditingField('priority');
            }}
            title="Klicken zum Ändern"
          >
            {priorityLabels[task.priority]}
          </span>
        )}
      </td>
      
      {/* Assigned To - Click to edit */}
      <td>
        {editingField === 'assigned' ? (
          <div onClick={(e) => e.stopPropagation()} className="inline-block">
            <Select
              value={task.assigned_to || 'unassigned'}
              onValueChange={(value) => {
                handleUpdateTask(task.id, { assigned_to: value === 'unassigned' ? null : value });
                setEditingField(null);
              }}
              open={true}
              onOpenChange={(open) => !open && setEditingField(null)}
            >
              <SelectTrigger className="h-8 text-xs w-36">
                <SelectValue placeholder="Nicht zugewiesen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Nicht zugewiesen</SelectItem>
                {getProjectUsers().map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name || u.username}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <span 
            className="cursor-pointer hover:text-blue-600 hover:underline inline-block px-1 py-0.5 rounded hover:bg-blue-50"
            onClick={(e) => {
              e.stopPropagation();
              setEditingField('assigned');
            }}
            title="Klicken zum Ändern"
          >
            {task.assigned_to_username || 'Nicht zugewiesen'}
          </span>
        )}
      </td>
      
      {/* Due Date - Click to edit */}
      <td>
        {editingField === 'due_date' ? (
          <div onClick={(e) => e.stopPropagation()} className="inline-block">
            <Input
              type="date"
              autoFocus
              value={task.due_date || ''}
              onChange={(e) => {
                handleUpdateTask(task.id, { due_date: e.target.value || null });
                setEditingField(null);
              }}
              onBlur={() => setEditingField(null)}
              className="h-8 text-xs w-32"
            />
          </div>
        ) : (
          <span 
            className="cursor-pointer hover:text-blue-600 hover:underline inline-block px-1 py-0.5 rounded hover:bg-blue-50"
            onClick={(e) => {
              e.stopPropagation();
              setEditingField('due_date');
            }}
            title="Klicken zum Ändern"
          >
            {task.due_date ? new Date(task.due_date).toLocaleDateString('de-DE') : '-'}
          </span>
        )}
      </td>
    </tr>
  );
};

const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [view, setView] = useState('list');
  const [isCreateTaskOpen, setIsCreateTaskOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    status: 'todo',
    priority: 'medium',
    due_date: '',
    assigned_to: 'unassigned'
  });
  const [newComment, setNewComment] = useState('');
  const [draggedTask, setDraggedTask] = useState(null);
  const [draggedOverColumn, setDraggedOverColumn] = useState(null);
  const [isManageUsersOpen, setIsManageUsersOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('unassigned');
  const [originalTaskValues, setOriginalTaskValues] = useState({});
  const [isAICreatorOpen, setIsAICreatorOpen] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  
  // Filter states - loaded from localStorage
  const [filters, setFilters] = useState(() => {
    const savedFilters = localStorage.getItem(`taskFilters_${projectId}`);
    return savedFilters ? JSON.parse(savedFilters) : {
      status: [],
      priority: [],
      assignedTo: [],
      createdBy: []
    };
  });
  
  // Batch selection for list view
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [batchActionOpen, setBatchActionOpen] = useState(false);

  useEffect(() => {
    fetchProject();
    fetchTasks();
    fetchUsers();
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`, {
        headers: getAuthHeader()
      });
      setProject(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden des Projekts');
      navigate('/');
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API}/tasks?project_id=${projectId}`, {
        headers: getAuthHeader()
      });
      setTasks(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Tasks');
    }
  };

  const fetchUsers = async () => {
    try {
      // Try to get all users (admin only)
      const response = await axios.get(`${API}/users`, {
        headers: getAuthHeader()
      });
      setUsers(response.data);
    } catch (error) {
      // If not admin, fetch project-specific users
      if (error.response?.status === 403) {
        try {
          const projectUsersResponse = await axios.get(`${API}/projects/${projectId}/users`, {
            headers: getAuthHeader()
          });
          setUsers(projectUsersResponse.data);
        } catch (e) {
          console.error('Fehler beim Laden der Projekt-Benutzer:', e);
        }
      } else {
        console.error('Fehler beim Laden der Benutzer:', error);
      }
    }
  };

  // Save filters to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(`taskFilters_${projectId}`, JSON.stringify(filters));
  }, [filters, projectId]);

  // Filter tasks based on active filters
  const getFilteredTasks = () => {
    return tasks.filter(task => {
      // Status filter
      if (filters.status.length > 0 && !filters.status.includes(task.status)) {
        return false;
      }
      
      // Priority filter
      if (filters.priority.length > 0 && !filters.priority.includes(task.priority)) {
        return false;
      }
      
      // Assigned to filter
      if (filters.assignedTo.length > 0) {
        if (filters.assignedTo.includes('unassigned') && !task.assigned_to) {
          // Include unassigned tasks
        } else if (!filters.assignedTo.includes(task.assigned_to)) {
          return false;
        }
      }
      
      // Created by filter
      if (filters.createdBy.length > 0 && !filters.createdBy.includes(task.created_by)) {
        return false;
      }
      
      return true;
    });
  };

  // Toggle filter option
  const toggleFilter = (filterType, value) => {
    setFilters(prev => {
      const currentValues = prev[filterType];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      
      return { ...prev, [filterType]: newValues };
    });
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      status: [],
      priority: [],
      assignedTo: [],
      createdBy: []
    });
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return filters.status.length > 0 || 
           filters.priority.length > 0 || 
           filters.assignedTo.length > 0 || 
           filters.createdBy.length > 0;
  };

  // Batch operations
  const toggleTaskSelection = (taskId) => {
    setSelectedTaskIds(prev => 
      prev.includes(taskId) 
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  const selectAllTasks = () => {
    const filteredTasks = getFilteredTasks();
    setSelectedTaskIds(filteredTasks.map(t => t.id));
  };

  const deselectAllTasks = () => {
    setSelectedTaskIds([]);
  };

  const handleBatchUpdate = async (updates) => {
    try {
      // Update all selected tasks
      await Promise.all(
        selectedTaskIds.map(taskId =>
          axios.put(`${API}/tasks/${taskId}`, updates, {
            headers: getAuthHeader()
          })
        )
      );
      
      toast.success(`${selectedTaskIds.length} Tasks aktualisiert`);
      fetchTasks();
      deselectAllTasks();
      setBatchActionOpen(false);
    } catch (error) {
      toast.error('Fehler beim Aktualisieren der Tasks');
    }
  };

  const handleBatchDelete = async () => {
    if (!window.confirm(`Möchten Sie ${selectedTaskIds.length} Tasks wirklich löschen?`)) {
      return;
    }
    
    try {
      await Promise.all(
        selectedTaskIds.map(taskId =>
          axios.delete(`${API}/tasks/${taskId}`, {
            headers: getAuthHeader()
          })
        )
      );
      
      toast.success(`${selectedTaskIds.length} Tasks gelöscht`);
      fetchTasks();
      deselectAllTasks();
    } catch (error) {
      toast.error('Fehler beim Löschen der Tasks');
    }
  };


  // Get users assigned to the current project
  const getProjectUsers = () => {
    if (!project) return [];
    return users.filter(u => project.assigned_users.includes(u.id));
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      const taskData = { ...newTask, project_id: projectId };
      // Handle unassigned case
      if (taskData.assigned_to === 'unassigned') {
        taskData.assigned_to = null;
      }
      
      await axios.post(`${API}/tasks`, taskData, {
        headers: getAuthHeader()
      });
      toast.success('Task erfolgreich erstellt!');
      setIsCreateTaskOpen(false);
      setNewTask({
        title: '',
        description: '',
        status: 'todo',
        priority: 'medium',
        due_date: '',
        assigned_to: 'unassigned'
      });
      fetchTasks();
    } catch (error) {
      toast.error('Fehler beim Erstellen des Tasks');
    }
  };

  const handleUpdateTask = async (taskId, updates) => {
    try {
      await axios.put(`${API}/tasks/${taskId}`, updates, {
        headers: getAuthHeader()
      });
      toast.success('Task aktualisiert!');
      fetchTasks();
      // Only reload the task details if the dialog is still open
      // We check this by seeing if selectedTask still has the same ID
      // This prevents the dialog from reopening when closing
      if (selectedTask?.id === taskId) {
        const response = await axios.get(`${API}/tasks/${taskId}`, {
          headers: getAuthHeader()
        });
        setSelectedTask(response.data);
      }
    } catch (error) {
      toast.error('Fehler beim Aktualisieren des Tasks');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Möchten Sie diesen Task wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/tasks/${taskId}`, {
        headers: getAuthHeader()
      });
      toast.success('Task gelöscht!');
      setSelectedTask(null);
      fetchTasks();
    } catch (error) {
      toast.error('Fehler beim Löschen des Tasks');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    // Check if user is already watching before adding comment
    const wasWatching = isWatchingTask(selectedTask);
    
    try {
      await axios.post(`${API}/tasks/${selectedTask.id}/comments`, {
        text: newComment
      }, {
        headers: getAuthHeader()
      });
      setNewComment('');
      const response = await axios.get(`${API}/tasks/${selectedTask.id}`, {
        headers: getAuthHeader()
      });
      setSelectedTask(response.data);
      toast.success('Kommentar hinzugefügt!');
      
      // If user wasn't watching before, they are now auto-watching
      // Check if email notifications are enabled
      if (!wasWatching && !user?.email_notifications_enabled) {
        setTimeout(() => {
          toast.warning(
            'Sie beobachten diesen Task jetzt automatisch. E-Mail-Benachrichtigungen sind jedoch deaktiviert. Aktivieren Sie diese in Ihrem Profil (Profil → E-Mail-Benachrichtigungen), um Updates zu erhalten.',
            { duration: 7000 }
          );
        }, 500);
      } else if (!wasWatching) {
        setTimeout(() => {
          toast.info('Sie beobachten diesen Task jetzt und erhalten Benachrichtigungen bei Änderungen.', {
            duration: 4000
          });
        }, 500);
      }
    } catch (error) {
      toast.error('Fehler beim Hinzufügen des Kommentars');
    }
  };

  const openTaskDetail = async (taskId) => {
    try {
      const response = await axios.get(`${API}/tasks/${taskId}`, {
        headers: getAuthHeader()
      });
      setSelectedTask(response.data);
      // Store original values for comparison
      setOriginalTaskValues({
        title: response.data.title,
        description: response.data.description
      });
    } catch (error) {
      toast.error('Fehler beim Laden des Tasks');
    }
  };

  const handleAddUserToProject = async () => {
    if (selectedUserId === 'unassigned') return;
    
    try {
      const updatedUsers = [...project.assigned_users, selectedUserId];
      await axios.put(`${API}/projects/${projectId}`, {
        assigned_users: updatedUsers
      }, {
        headers: getAuthHeader()
      });
      toast.success('Benutzer zum Projekt hinzugefügt!');
      setSelectedUserId('unassigned');
      fetchProject();
    } catch (error) {
      toast.error('Fehler beim Hinzufügen des Benutzers');
    }
  };

  const handleRemoveUserFromProject = async (userId) => {
    try {
      const updatedUsers = project.assigned_users.filter(id => id !== userId);
      await axios.put(`${API}/projects/${projectId}`, {
        assigned_users: updatedUsers
      }, {
        headers: getAuthHeader()
      });
      toast.success('Benutzer vom Projekt entfernt!');
      fetchProject();
    } catch (error) {
      toast.error('Fehler beim Entfernen des Benutzers');
    }
  };

  // Drag & Drop handlers
  const handleDragStart = (e, task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget);
  };

  const handleDragOver = (e, status) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDraggedOverColumn(status);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDraggedOverColumn(null);
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    setDraggedOverColumn(null);

    if (!draggedTask || draggedTask.status === newStatus) {
      setDraggedTask(null);
      return;
    }

    try {
      await handleUpdateTask(draggedTask.id, { status: newStatus });
      toast.success(`Task zu "${statusLabels[newStatus]}" verschoben`);
    } catch (error) {
      toast.error('Fehler beim Verschieben des Tasks');
    }

    setDraggedTask(null);
  };

  const handleDragEnd = () => {
    setDraggedTask(null);
    setDraggedOverColumn(null);
  };

  const handleAttachmentUpload = async (taskId, file) => {
    setUploadingAttachment(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/tasks/${taskId}/attachments`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Anhang hochgeladen!');
      
      // Reload task details with a small delay to ensure backend is ready
      await new Promise(resolve => setTimeout(resolve, 500));
      const response = await axios.get(`${API}/tasks/${taskId}`, {
        headers: getAuthHeader()
      });
      setSelectedTask(response.data);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Fehler beim Hochladen des Anhangs');
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleAttachmentDownload = async (taskId, attachmentId, filename) => {
    try {
      const response = await axios.get(
        `${API}/tasks/${taskId}/attachments/${attachmentId}`,
        {
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Fehler beim Herunterladen des Anhangs');
    }
  };

  const handleAttachmentDelete = async (taskId, attachmentId, filename) => {
    if (!window.confirm(`Möchten Sie den Anhang "${filename}" wirklich löschen?`)) return;
    
    try {
      await axios.delete(`${API}/tasks/${taskId}/attachments/${attachmentId}`, {
        headers: getAuthHeader()
      });
      
      toast.success('Anhang gelöscht!');
      
      // Reload task details
      const response = await axios.get(`${API}/tasks/${taskId}`, {
        headers: getAuthHeader()
      });
      setSelectedTask(response.data);
    } catch (error) {
      toast.error('Fehler beim Löschen des Anhangs');
    }
  };

  const handleWatchTask = async (taskId) => {
    try {
      await axios.post(`${API}/tasks/${taskId}/watch`, {}, {
        headers: getAuthHeader()
      });
      
      // Update selectedTask to reflect watcher change
      const response = await axios.get(`${API}/tasks/${taskId}`, {
        headers: getAuthHeader()
      });
      setSelectedTask(response.data);
      
      // Update tasks list
      fetchTasks();
      
      toast.success('Sie beobachten diesen Task jetzt');
      
      // Check if email notifications are enabled
      if (!user?.email_notifications_enabled) {
        setTimeout(() => {
          toast.warning(
            'E-Mail-Benachrichtigungen sind deaktiviert. Aktivieren Sie diese in Ihrem Profil, um Benachrichtigungen zu erhalten.',
            { duration: 6000 }
          );
        }, 500);
      }
    } catch (error) {
      toast.error('Fehler beim Beobachten des Tasks');
    }
  };

  const handleUnwatchTask = async (taskId) => {
    try {
      await axios.delete(`${API}/tasks/${taskId}/watch`, {
        headers: getAuthHeader()
      });
      
      // Update selectedTask to reflect watcher change
      const response = await axios.get(`${API}/tasks/${taskId}`, {
        headers: getAuthHeader()
      });
      setSelectedTask(response.data);
      
      // Update tasks list
      fetchTasks();
      
      toast.success('Sie beobachten diesen Task nicht mehr');
    } catch (error) {
      toast.error('Fehler beim Entfernen der Beobachtung');
    }
  };

  const isWatchingTask = (task) => {
    if (!task || !task.watchers || !user) return false;
    return task.watchers.includes(user.id);
  };

  const getAttachmentUrl = (taskId, attachmentId) => {
    return `${API}/tasks/${taskId}/attachments/${attachmentId}`;
  };

  const isImageFile = (mimeType) => {
    return mimeType && mimeType.startsWith('image/');
  };

  const getFileIcon = (mimeType) => {
    if (!mimeType) return '📄';
    if (mimeType.startsWith('image/')) return '🖼️';
    if (mimeType.startsWith('video/')) return '🎥';
    if (mimeType.startsWith('audio/')) return '🎵';
    if (mimeType.includes('pdf')) return '📕';
    if (mimeType.includes('word') || mimeType.includes('document')) return '📘';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📗';
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return '📙';
    if (mimeType.includes('zip') || mimeType.includes('archive')) return '🗜️';
    return '📄';
  };

  const handleAttachmentClick = (attachment, taskId) => {
    if (isImageFile(attachment.mime_type)) {
      // Open in lightbox
      setLightboxImage({
        url: getAttachmentUrl(taskId, attachment.id),
        filename: attachment.filename
      });
      setLightboxOpen(true);
    } else {
      // Download file
      handleAttachmentDownload(taskId, attachment.id, attachment.filename);
    }
  };


  if (!project) return <div>Laden...</div>;

  const filteredTasks = getFilteredTasks();
  
  const tasksByStatus = {
    todo: filteredTasks.filter(t => t.status === 'todo'),
    in_progress: filteredTasks.filter(t => t.status === 'in_progress'),
    review: filteredTasks.filter(t => t.status === 'review'),
    done: filteredTasks.filter(t => t.status === 'done')
  };

  return (
    <div className="min-h-screen bg-[#f6f7fb]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                data-testid="back-to-dashboard-button"
                onClick={() => navigate('/')}
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Zurück</span>
              </Button>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: project.color }}>
                  <span className="text-white font-bold">{project.name.charAt(0).toUpperCase()}</span>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800">{project.name}</h1>
                  {project.description && (
                    <p className="text-sm text-gray-500">{project.description}</p>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Tabs value={view} onValueChange={setView}>
                <TabsList>
                  <TabsTrigger value="list" data-testid="list-view-tab" className="flex items-center space-x-2">
                    <List className="w-4 h-4" />
                    <span>Liste</span>
                  </TabsTrigger>
                  <TabsTrigger value="kanban" data-testid="kanban-view-tab" className="flex items-center space-x-2">
                    <Columns3 className="w-4 h-4" />
                    <span>Kanban</span>
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              {user?.role === 'admin' && (
                <>
                  <Button
                    variant="outline"
                    data-testid="ai-task-creator-button"
                    onClick={() => setIsAICreatorOpen(true)}
                    className="flex items-center space-x-2 bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200 hover:from-purple-100 hover:to-blue-100"
                  >
                    <Sparkles className="w-4 h-4 text-purple-600" />
                    <span className="text-purple-700">AI Task Creator</span>
                  </Button>
                  <Button
                    variant="outline"
                    data-testid="manage-users-button"
                    onClick={() => setIsManageUsersOpen(true)}
                    className="flex items-center space-x-2"
                  >
                    <User className="w-4 h-4" />
                    <span>Benutzer verwalten</span>
                  </Button>
                </>
              )}
              <Button
                data-testid="create-task-button"
                onClick={() => setIsCreateTaskOpen(true)}
                className="bg-[#0073ea] hover:bg-[#0060c0] text-white flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>Neuer Task</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Filter Bar */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center space-x-2">
              <Filter className="w-5 h-5 text-gray-600" />
              <span className="font-semibold text-gray-700">Filter:</span>
              
              {/* Status Filter */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Status:</span>
                {Object.entries(statusLabels).map(([value, label]) => (
                  <button
                    key={value}
                    onClick={() => toggleFilter('status', value)}
                    className={`px-3 py-1 text-xs rounded-full transition-colors ${
                      filters.status.includes(value)
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              
              {/* Priority Filter */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Priorität:</span>
                {Object.entries(priorityLabels).map(([value, label]) => (
                  <button
                    key={value}
                    onClick={() => toggleFilter('priority', value)}
                    className={`px-3 py-1 text-xs rounded-full transition-colors ${
                      filters.priority.includes(value)
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              
              {/* Assigned To Filter */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Zugewiesen:</span>
                <button
                  onClick={() => toggleFilter('assignedTo', 'unassigned')}
                  className={`px-3 py-1 text-xs rounded-full transition-colors ${
                    filters.assignedTo.includes('unassigned')
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Nicht zugewiesen
                </button>
                {getProjectUsers().map((u) => (
                  <button
                    key={u.id}
                    onClick={() => toggleFilter('assignedTo', u.id)}
                    className={`px-3 py-1 text-xs rounded-full transition-colors ${
                      filters.assignedTo.includes(u.id)
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {u.username}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {hasActiveFilters() && (
                <>
                  <span className="text-sm text-gray-600">
                    {filteredTasks.length} von {tasks.length} Tasks
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearFilters}
                    className="text-xs"
                  >
                    Filter zurücksetzen
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Batch Actions Bar (List View Only) */}
        {view === 'list' && selectedTaskIds.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <CheckSquare className="w-5 h-5 text-blue-600" />
                <span className="font-semibold text-gray-700">
                  {selectedTaskIds.length} Task(s) ausgewählt
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Select onValueChange={(value) => {
                  if (value === 'delete') {
                    handleBatchDelete();
                  } else {
                    setBatchActionOpen(true);
                  }
                }}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Aktion wählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="status">Status ändern</SelectItem>
                    <SelectItem value="priority">Priorität ändern</SelectItem>
                    <SelectItem value="assign">Zuweisen</SelectItem>
                    <SelectItem value="delete">Löschen</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={deselectAllTasks}
                >
                  Auswahl aufheben
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* List View */}
        {view === 'list' && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <table className="task-table">
              <thead>
                <tr>
                  <th className="w-12">
                    <input
                      type="checkbox"
                      checked={selectedTaskIds.length === filteredTasks.length && filteredTasks.length > 0}
                      onChange={(e) => e.target.checked ? selectAllTasks() : deselectAllTasks()}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                  </th>
                  <th>Task</th>
                  <th>Status</th>
                  <th>Priorität</th>
                  <th>Zugewiesen an</th>
                  <th>Fälligkeitsdatum</th>
                </tr>
              </thead>
              <tbody>
                {filteredTasks.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-8 text-gray-500">
                      {hasActiveFilters() 
                        ? 'Keine Tasks entsprechen den aktiven Filtern.'
                        : 'Keine Tasks vorhanden. Erstellen Sie Ihren ersten Task!'
                      }
                    </td>
                  </tr>
                ) : (
                  filteredTasks.map((task) => (
                    <TaskRow 
                      key={task.id}
                      task={task}
                      selectedTaskIds={selectedTaskIds}
                      toggleTaskSelection={toggleTaskSelection}
                      handleUpdateTask={handleUpdateTask}
                      openTaskDetail={openTaskDetail}
                      statusLabels={statusLabels}
                      priorityLabels={priorityLabels}
                      getProjectUsers={getProjectUsers}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Kanban View */}
        {view === 'kanban' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Object.entries(tasksByStatus).map(([status, statusTasks]) => (
              <div
                key={status}
                className={`kanban-column ${draggedOverColumn === status ? 'ring-2 ring-blue-400 ring-offset-2' : ''}`}
                onDragOver={(e) => handleDragOver(e, status)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, status)}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-gray-700 flex items-center space-x-2">
                    <span className={`w-3 h-3 rounded-full ${
                      status === 'todo' ? 'bg-gray-400' : 
                      status === 'in_progress' ? 'bg-orange-400' : 
                      status === 'review' ? 'bg-blue-400' : 
                      'bg-green-400'
                    }`}></span>
                    <span>{statusLabels[status]}</span>
                  </h3>
                  <span className="text-sm text-gray-500">{statusTasks.length}</span>
                </div>
                <div className="space-y-3 min-h-[200px]">
                  {statusTasks.map((task) => (
                    <div
                      key={task.id}
                      data-testid={`kanban-card-${task.id}`}
                      className={`kanban-card cursor-grab active:cursor-grabbing ${
                        draggedTask?.id === task.id ? 'opacity-50' : ''
                      }`}
                      style={{ borderLeftColor: project.color }}
                      draggable
                      onDragStart={(e) => handleDragStart(e, task)}
                      onDragEnd={handleDragEnd}
                      onClick={(e) => {
                        // Prevent opening detail when dragging
                        if (!draggedTask) {
                          openTaskDetail(task.id);
                        }
                      }}
                    >
                      <h4 className="font-semibold text-gray-800 mb-3">{task.title}</h4>
                      <div className="flex items-center justify-between">
                        <span className={`status-badge priority-${task.priority}`}>
                          {priorityLabels[task.priority]}
                        </span>
                        {task.assigned_to_username && (
                          <span className="text-xs text-gray-500">{task.assigned_to_username}</span>
                        )}
                      </div>
                    </div>
                  ))}
                  {statusTasks.length === 0 && draggedOverColumn === status && (
                    <div 
                      className="text-center py-8 text-gray-400 border-2 border-dashed border-gray-300 rounded-lg"
                      style={{ pointerEvents: 'none' }}
                    >
                      Hier ablegen
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Create Task Dialog */}
      <Dialog open={isCreateTaskOpen} onOpenChange={setIsCreateTaskOpen}>
        <DialogContent className="sm:max-w-[600px]" aria-describedby="dialog-task-description">
          <DialogHeader>
            <DialogTitle>Neuen Task erstellen</DialogTitle>
            <DialogDescription id="dialog-task-description">
              Erstellen Sie einen neuen Task für dieses Projekt.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateTask} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="task-title">Titel</Label>
              <Input
                id="task-title"
                data-testid="task-title-input"
                value={newTask.title}
                onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                required
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="task-description">Beschreibung</Label>
              <Textarea
                id="task-description"
                data-testid="task-description-input"
                value={newTask.description}
                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                className="mt-2"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="task-status">Status</Label>
                <Select
                  value={newTask.status}
                  onValueChange={(value) => setNewTask({ ...newTask, status: value })}
                >
                  <SelectTrigger className="mt-2" data-testid="task-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(statusLabels).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="task-priority">Priorität</Label>
                <Select
                  value={newTask.priority}
                  onValueChange={(value) => setNewTask({ ...newTask, priority: value })}
                >
                  <SelectTrigger className="mt-2" data-testid="task-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(priorityLabels).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="task-assigned">Zugewiesen an</Label>
                <Select
                  value={newTask.assigned_to}
                  onValueChange={(value) => setNewTask({ ...newTask, assigned_to: value })}
                >
                  <SelectTrigger className="mt-2" data-testid="task-assigned-select">
                    <SelectValue placeholder="Wählen Sie einen Benutzer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Nicht zugewiesen</SelectItem>
                    {getProjectUsers().map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name || u.username} {u.email ? `(${u.email})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="task-due-date">Fälligkeitsdatum</Label>
                <Input
                  id="task-due-date"
                  data-testid="task-due-date-input"
                  type="date"
                  value={newTask.due_date}
                  onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                  className="mt-2"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsCreateTaskOpen(false)}>
                Abbrechen
              </Button>
              <Button type="submit" data-testid="create-task-submit-button" className="bg-[#0073ea] hover:bg-[#0060c0] text-white">
                Erstellen
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Task Detail Dialog */}
      {selectedTask && (
        <Dialog open={!!selectedTask} onOpenChange={() => setSelectedTask(null)}>
          <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto" aria-describedby="dialog-task-detail-description">
            <DialogHeader>
              <DialogTitle>Task Details</DialogTitle>
              <DialogDescription id="dialog-task-detail-description">
                Bearbeiten Sie die Task-Details und fügen Sie Kommentare hinzu.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6 mt-4">
              <div>
                <Label>Titel</Label>
                <Input
                  value={selectedTask.title}
                  data-testid="task-detail-title-input"
                  onChange={(e) => setSelectedTask({ ...selectedTask, title: e.target.value })}
                  onBlur={() => {
                    if (selectedTask.title !== originalTaskValues.title) {
                      handleUpdateTask(selectedTask.id, { title: selectedTask.title });
                      setOriginalTaskValues({ ...originalTaskValues, title: selectedTask.title });
                    }
                  }}
                  className="mt-2"
                />
              </div>
              <div>
                <Label>Beschreibung</Label>
                <Textarea
                  value={selectedTask.description}
                  data-testid="task-detail-description-input"
                  onChange={(e) => setSelectedTask({ ...selectedTask, description: e.target.value })}
                  onBlur={() => {
                    if (selectedTask.description !== originalTaskValues.description) {
                      handleUpdateTask(selectedTask.id, { description: selectedTask.description });
                      setOriginalTaskValues({ ...originalTaskValues, description: selectedTask.description });
                    }
                  }}
                  className="mt-2"
                  rows={4}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Status</Label>
                  <Select
                    value={selectedTask.status}
                    onValueChange={(value) => {
                      setSelectedTask({ ...selectedTask, status: value });
                      handleUpdateTask(selectedTask.id, { status: value });
                    }}
                  >
                    <SelectTrigger className="mt-2" data-testid="task-detail-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(statusLabels).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priorität</Label>
                  <Select
                    value={selectedTask.priority}
                    onValueChange={(value) => {
                      setSelectedTask({ ...selectedTask, priority: value });
                      handleUpdateTask(selectedTask.id, { priority: value });
                    }}
                  >
                    <SelectTrigger className="mt-2" data-testid="task-detail-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(priorityLabels).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Zugewiesen an</Label>
                  <Select
                    value={selectedTask.assigned_to || 'unassigned'}
                    onValueChange={(value) => {
                      const assignedValue = value === 'unassigned' ? null : value;
                      setSelectedTask({ ...selectedTask, assigned_to: assignedValue });
                      handleUpdateTask(selectedTask.id, { assigned_to: assignedValue });
                    }}
                  >
                    <SelectTrigger className="mt-2" data-testid="task-detail-assigned-select">
                      <SelectValue placeholder="Nicht zugewiesen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Nicht zugewiesen</SelectItem>
                      {getProjectUsers().map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.name || u.username} {u.email ? `(${u.email})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Fälligkeitsdatum</Label>
                  <Input
                    type="date"
                    data-testid="task-detail-due-date-input"
                    value={selectedTask.due_date || ''}
                    onChange={(e) => {
                      setSelectedTask({ ...selectedTask, due_date: e.target.value });
                      handleUpdateTask(selectedTask.id, { due_date: e.target.value });
                    }}
                    className="mt-2"
                  />
                </div>
              </div>

              {/* Attachments Section */}
              <div className="border-t pt-6">
                <h3 className="font-semibold text-gray-800 mb-4 flex items-center space-x-2">
                  <Paperclip className="w-4 h-4" />
                  <span>Anhänge ({selectedTask.attachments?.length || 0})</span>
                </h3>
                
                {/* Existing Attachments - Grid View with Thumbnails */}
                {selectedTask.attachments && selectedTask.attachments.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                    {selectedTask.attachments.map((attachment) => (
                      <div
                        key={attachment.id}
                        className="relative group cursor-pointer"
                        onClick={() => handleAttachmentClick(attachment, selectedTask.id)}
                      >
                        {/* Thumbnail or Icon */}
                        <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 border-2 border-gray-200 hover:border-blue-400 transition-all">
                          {isImageFile(attachment.mime_type) ? (
                            <ImageWithAuth
                              src={getAttachmentUrl(selectedTask.id, attachment.id)}
                              alt={attachment.filename}
                              className="w-full h-full object-cover"
                              getAuthHeader={getAuthHeader}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-5xl">
                              {getFileIcon(attachment.mime_type)}
                            </div>
                          )}
                        </div>
                        
                        {/* Filename Overlay */}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                          <p className="text-xs text-white truncate font-medium">
                            {attachment.filename}
                          </p>
                          <p className="text-xs text-gray-300">
                            {(attachment.size / 1024).toFixed(2)} KB
                          </p>
                        </div>

                        {/* Download Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAttachmentDownload(selectedTask.id, attachment.id, attachment.filename);
                          }}
                          className="absolute top-2 right-10 bg-white/90 hover:bg-white p-1.5 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Download className="w-4 h-4 text-gray-700" />
                        </button>
                        
                        {/* Delete Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAttachmentDelete(selectedTask.id, attachment.id, attachment.filename);
                          }}
                          className="absolute top-2 right-2 bg-red-500/90 hover:bg-red-600 p-1.5 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="w-4 h-4 text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload New Attachment */}
                <div>
                  <input
                    type="file"
                    id={`attachment-upload-${selectedTask.id}`}
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleAttachmentUpload(selectedTask.id, file);
                        e.target.value = '';
                      }
                    }}
                  />
                  <Button
                    variant="outline"
                    disabled={uploadingAttachment}
                    onClick={() => document.getElementById(`attachment-upload-${selectedTask.id}`).click()}
                    className="w-full flex items-center justify-center space-x-2"
                  >
                    {uploadingAttachment ? (
                      <>
                        <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
                        <span>Hochladen...</span>
                      </>
                    ) : (
                      <>
                        <Paperclip className="w-4 h-4" />
                        <span>Anhang hinzufügen</span>
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Watch/Unwatch Button */}
              <div className="border-t pt-6">
                {selectedTask.assigned_to === user.id ? (
                  <div className="flex items-center space-x-2 p-3 bg-blue-50 rounded-lg">
                    <Bell className="w-5 h-5 text-blue-600" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-800">Sie erhalten Benachrichtigungen zu diesem Task</p>
                      <p className="text-xs text-gray-500">Sie sind diesem Task zugewiesen</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <Button
                      onClick={() => isWatchingTask(selectedTask) ? handleUnwatchTask(selectedTask.id) : handleWatchTask(selectedTask.id)}
                      variant={isWatchingTask(selectedTask) ? "default" : "outline"}
                      className={`w-full ${isWatchingTask(selectedTask) ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}`}
                    >
                      {isWatchingTask(selectedTask) ? (
                        <>
                          <Bell className="w-4 h-4 mr-2" />
                          Task wird beobachtet - Benachrichtigungen aktiv
                        </>
                      ) : (
                        <>
                          <BellOff className="w-4 h-4 mr-2" />
                          Task beobachten - Benachrichtigungen erhalten
                        </>
                      )}
                    </Button>
                    
                    {/* Email notification warning */}
                    {!user?.email_notifications_enabled && (
                      <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-xs text-amber-800">
                          ⚠️ E-Mail-Benachrichtigungen sind deaktiviert. 
                          <button
                            onClick={() => navigate('/profile')}
                            className="ml-1 underline font-medium hover:text-amber-900"
                          >
                            Aktivieren Sie diese in Ihrem Profil
                          </button>
                          , um Benachrichtigungen zu erhalten.
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Comments Section */}
              <div className="border-t pt-6">
                <h3 className="font-semibold text-gray-800 mb-4 flex items-center space-x-2">
                  <MessageSquare className="w-4 h-4" />
                  <span>Kommentare ({selectedTask.comments.length})</span>
                </h3>
                <div className="space-y-3 mb-4 max-h-60 overflow-y-auto">
                  {selectedTask.comments.length === 0 ? (
                    <p className="text-sm text-gray-500">Noch keine Kommentare</p>
                  ) : (
                    selectedTask.comments.map((comment) => (
                      <div key={comment.id} className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-gray-800">{comment.username}</span>
                          <span className="text-xs text-gray-500">
                            {new Date(comment.created_at).toLocaleString('de-DE')}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700">{comment.text}</p>
                      </div>
                    ))
                  )}
                </div>
                <div className="flex space-x-2">
                  <Textarea
                    placeholder="Kommentar hinzufügen..."
                    data-testid="task-comment-input"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    rows={2}
                    className="flex-1"
                  />
                  <Button
                    data-testid="add-comment-button"
                    onClick={handleAddComment}
                    className="bg-[#0073ea] hover:bg-[#0060c0] text-white"
                  >
                    Senden
                  </Button>
                </div>
              </div>

              {/* Metadata */}
              <div className="border-t pt-4 text-sm text-gray-500 space-y-1">
                <p>Erstellt von: {selectedTask.created_by_username}</p>
                <p>Erstellt am: {new Date(selectedTask.created_at).toLocaleString('de-DE')}</p>
                <p>Zuletzt aktualisiert: {new Date(selectedTask.updated_at).toLocaleString('de-DE')}</p>
              </div>

              {/* Delete Task Button - Admins can delete any, Kunden can delete their own */}
              {(user?.role === 'admin' || selectedTask.created_by === user?.id) && (
                <div className="border-t pt-4">
                  <Button
                    variant="outline"
                    data-testid="delete-task-button"
                    onClick={() => handleDeleteTask(selectedTask.id)}
                    className="w-full text-red-500 hover:text-red-700 hover:bg-red-50 border-red-200"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Task löschen
                  </Button>
                  {user?.role !== 'admin' && (
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      Sie können nur Ihre eigenen Tasks löschen
                    </p>
                  )}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Manage Users Dialog */}
      {user?.role === 'admin' && (
        <Dialog open={isManageUsersOpen} onOpenChange={setIsManageUsersOpen}>
          <DialogContent className="sm:max-w-[500px]" aria-describedby="dialog-manage-users-description">
            <DialogHeader>
              <DialogTitle>Benutzer verwalten</DialogTitle>
              <DialogDescription id="dialog-manage-users-description">
                Fügen Sie Benutzer zum Projekt hinzu oder entfernen Sie sie.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 mt-4">
              {/* Current Users */}
              <div>
                <Label className="text-base font-semibold">Zugewiesene Benutzer</Label>
                <div className="mt-3 space-y-2">
                  {project?.assigned_users.length === 0 ? (
                    <p className="text-sm text-gray-500">Keine Benutzer zugewiesen</p>
                  ) : (
                    project?.assigned_users.map((userId) => {
                      const assignedUser = users.find(u => u.id === userId);
                      return assignedUser ? (
                        <div
                          key={userId}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-400 to-purple-500 flex items-center justify-center">
                              <span className="text-white text-sm font-semibold">
                                {assignedUser.username.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-800">{assignedUser.username}</p>
                              <p className="text-xs text-gray-500">{assignedUser.email}</p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`remove-user-${userId}`}
                            onClick={() => handleRemoveUserFromProject(userId)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            Entfernen
                          </Button>
                        </div>
                      ) : null;
                    })
                  )}
                </div>
              </div>

              {/* Add User */}
              <div className="border-t pt-4">
                <Label htmlFor="add-user">Benutzer hinzufügen</Label>
                <div className="flex space-x-2 mt-2">
                  <Select
                    value={selectedUserId}
                    onValueChange={setSelectedUserId}
                  >
                    <SelectTrigger className="flex-1" data-testid="add-user-select">
                      <SelectValue placeholder="Benutzer auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Benutzer auswählen</SelectItem>
                      {users
                        .filter(u => !project?.assigned_users.includes(u.id))
                        .map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.username} ({u.email}) {u.role === 'admin' ? '- Admin' : ''}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={handleAddUserToProject}
                    data-testid="add-user-button"
                    disabled={selectedUserId === 'unassigned'}
                    className="bg-[#0073ea] hover:bg-[#0060c0] text-white"
                  >
                    Hinzufügen
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}


      {/* Batch Action Dialog */}
      <Dialog open={batchActionOpen} onOpenChange={setBatchActionOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Batch-Aktion durchführen</DialogTitle>
            <DialogDescription>
              Änderungen auf {selectedTaskIds.length} Task(s) anwenden
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label>Status ändern</Label>
              <Select onValueChange={(value) => handleBatchUpdate({ status: value })}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Status wählen..." />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(statusLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Priorität ändern</Label>
              <Select onValueChange={(value) => handleBatchUpdate({ priority: value })}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Priorität wählen..." />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(priorityLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Zuweisen an</Label>
              <Select onValueChange={(value) => handleBatchUpdate({ assigned_to: value === 'unassigned' ? null : value })}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Benutzer wählen..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Nicht zugewiesen</SelectItem>
                  {getProjectUsers().map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name || u.username} {u.email ? `(${u.email})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end space-x-2 mt-6">
            <Button variant="outline" onClick={() => setBatchActionOpen(false)}>
              Abbrechen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Task Creator Dialog */}
      <AITaskCreator
        isOpen={isAICreatorOpen}
        onClose={() => setIsAICreatorOpen(false)}
        projectId={projectId}
        getAuthHeader={getAuthHeader}
        onTasksCreated={fetchTasks}
      />

      {/* Lightbox for Image Preview */}
      {lightboxOpen && lightboxImage && (
        <div 
          className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
            onClick={() => setLightboxOpen(false)}
          >
            <X className="w-8 h-8" />
          </button>
          
          <div className="max-w-7xl max-h-[90vh] flex flex-col items-center">
            <ImageWithAuth
              src={lightboxImage.url}
              alt={lightboxImage.filename}
              className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
              getAuthHeader={getAuthHeader}
              onClick={(e) => e.stopPropagation()}
            />
            <div className="mt-4 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
              <p className="text-white font-medium text-center">{lightboxImage.filename}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetail;
